import { invalidateCache, updateCachedValue } from './cache';
import { invalidateGraveCache } from './graveCache';
import store from '../redux/store';

// Map resource prefixes to patchers that reconcile Redux cache data with server changes
const RESOURCE_PATCHERS = {
  '/api/user_connections': (current, payload) => {
    const list = Array.isArray(current?.user_connections) ? current.user_connections : [];
    const action = payload?.action;
    const record = payload?.record;
    const targetId = Number(payload?.id || record?.id);

    if (!action || !targetId) return current;

    if (action === 'deleted') {
      const nextList = list.filter((item) => Number(item?.id) !== targetId);
      return { ...current, user_connections: nextList };
    }

    if (action === 'created' || action === 'updated') {
      if (!record || typeof record !== 'object') return current;
      const idx = list.findIndex((item) => Number(item?.id) === targetId);
      if (idx === -1) {
        return { ...current, user_connections: [...list, record] };
      }
      const nextList = [...list];
      nextList[idx] = { ...list[idx], ...record };
      return { ...current, user_connections: nextList };
    }

    return current;
  },
  '/api/user_personalities': (current, payload) => {
    const list = Array.isArray(current?.user_personalities)
      ? current.user_personalities
      : [];
    const action = payload?.action;
    const record = payload?.record?.user_personality || payload?.record;
    const targetId = Number(payload?.id || record?.id);

    if (!action || !targetId) return current;

    if (action === 'deleted') {
      const nextList = list.filter((item) => Number(item?.id) !== targetId);
      return { ...current, user_personalities: nextList };
    }

    if (action === 'created' || action === 'updated') {
      if (!record || typeof record !== 'object') return current;
      const idx = list.findIndex((item) => Number(item?.id) === targetId);
      if (idx === -1) {
        return { ...current, user_personalities: [...list, record] };
      }
      const nextList = [...list];
      nextList[idx] = { ...list[idx], ...record };
      return { ...current, user_personalities: nextList };
    }

    return current;
  },
  '/api/users': (current, payload) => {
    const record = payload?.record?.user || payload?.record;
    if (!record || typeof record !== 'object') return current || null;
    // If current is shaped like { user: {...} }, merge into user; otherwise return wrapped user.
    if (current && typeof current === 'object' && 'user' in current) {
      return { ...current, user: { ...(current.user || {}), ...record } };
    }
    return { user: record };
  },
  '/api/grave_invitations': (current, payload) => {
    const list = Array.isArray(current) ? current : Array.isArray(current?.grave_invitations) ? current.grave_invitations : [];
    const action = payload?.action;
    const targetId = Number(payload?.id);
    if (!action || !targetId) return current;

    if (action === 'deleted') {
      const next = list.filter((inv) => Number(inv?.id) !== targetId);
      return Array.isArray(current) ? next : { grave_invitations: next };
    }

    // For created/updated we expect a refetch; keep current as-is to avoid stale append.
    return current;
  },
};

const expandKeys = (key) => {
  const result = new Set();
  if (!key) return result;
  result.add(key);

  const base = key.split('?')[0];
  if (base && base !== key) result.add(base);

  const apiPrefixed = key.startsWith('/api') ? key : `/api${key.startsWith('/') ? '' : '/'}${key}`;
  result.add(apiPrefixed);
  if (base && base !== key) {
    const apiBase = base.startsWith('/api') ? base : `/api${base.startsWith('/') ? '' : '/'}${base}`;
    result.add(apiBase);
  }

  return result;
};

const normalizeKeys = (payload) => {
  const keys = new Set();
  if (payload?.resource) keys.add(payload.resource);
  if (Array.isArray(payload?.keys)) {
    payload.keys.forEach((k) => {
      if (!k) return;
      keys.add(k);
      try {
        keys.add(decodeURIComponent(k));
      } catch {
        /* ignore */
      }
    });
  }
  return keys;
};

const augmentKeysWithResourceId = (resource, id) => {
  const keys = new Set();
  if (!resource || !id) return keys;

  const ensureApiPrefix = (value) => {
    if (!value) return value;
    return value.startsWith('/api') ? value : `/api${value.startsWith('/') ? '' : '/'}${value}`;
  };

  const apiResource = ensureApiPrefix(resource);
  const bareResource = apiResource.replace(/^\/api/, '') || resource;

  // Grave detail invalidations (id should be slug)
  if (apiResource.startsWith('/api/graves')) {
    keys.add(apiResource);
    keys.add(bareResource);
    keys.add(`/api/graves/${id}`);
    keys.add(`/graves/${id}`);
  }

  // Memory detail/reaction endpoints
  if (apiResource === '/api/memories') {
    keys.add(`${apiResource}/${id}`);
    keys.add(`${bareResource}/${id}`);
    keys.add(`${apiResource}/${id}/reaction`);
    keys.add(`${bareResource}/${id}/reaction`);
  }

  // Individual comment endpoint
  if (apiResource === '/api/comments') {
    keys.add(`${apiResource}/${id}`);
    keys.add(`${bareResource}/${id}`);
  }

  return keys;
};

const patchCacheIfSupported = (key, payload) => {
  const entry = [...Object.keys(RESOURCE_PATCHERS)].find((prefix) => key.startsWith(prefix));
  if (!entry) return;
  const patcher = RESOURCE_PATCHERS[entry];
  updateCachedValue(key, (current) => patcher(current, payload));
  // Also patch bare key if the prefixed key was used
  if (key.startsWith('/api')) {
    const bare = key.replace(/^\/api/, '');
    updateCachedValue(bare, (current) => patcher(current, payload));
  }
};

export const handleCacheInvalidationMessage = (rawPayload) => {
  let payload = rawPayload;
  try {
    payload =
      typeof rawPayload === 'string' && rawPayload.trim().length
        ? JSON.parse(rawPayload)
        : rawPayload;
  } catch (error) {
    console.warn('[CacheInvalidation] Failed to parse payload', error);
    return;
  }

  // TEMP: trace invalidation handling during verification
  try {
    console.info('[cache-invalidate]', payload);
  } catch {}

  const keys = normalizeKeys(payload);
  if (!keys.size) return;

  const derivedKeys = augmentKeysWithResourceId(payload?.resource, payload?.id);
  derivedKeys.forEach((k) => keys.add(k));

  const expandedKeys = new Set();
  keys.forEach((key) => {
    expandKeys(key).forEach((k) => expandedKeys.add(k));
  });

  expandedKeys.forEach((key) => {
    invalidateCache(key, { exact: false });
    patchCacheIfSupported(key, payload);
    // Also patch/invalidate bare keys to cover fetch_request cacheKey variants
    if (key.startsWith('/api')) {
      const bare = key.replace(/^\/api/, '');
      invalidateCache(bare, { exact: false });
      patchCacheIfSupported(bare, payload);
    }

    if (key.startsWith('/api/graves/')) {
      const slug = key.split('/api/graves/')[1]?.split('?')[0];
      if (slug) {
        try {
          invalidateGraveCache(slug);
        } catch {}
      }
    }
  });

  // Broadcast a browser event so views can refetch if they rely on local state
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(
        new CustomEvent('cache-invalidate', {
          detail: { payload, keys: Array.from(expandedKeys) },
        }),
      );
      // Handle slug changes: if current URL matches an old grave slug, redirect to the new slug
      const recordSlug = payload?.record?.slug;
      const resource = payload?.resource;
      if (
        recordSlug &&
        typeof window.location?.pathname === 'string' &&
        resource &&
        String(resource).startsWith('/api/graves/')
      ) {
        const currentPath = window.location.pathname || '';
        const search = window.location.search || '';

        // Case 1: direct grave detail /graves/<slug>
        const currentSlugMatch = currentPath.match(/\/graves\/([^/?#]+)/);
        const currentSlug = currentSlugMatch && currentSlugMatch[1] ? currentSlugMatch[1] : null;
        const resourceSlug = String(resource).replace(/^\/api\/graves\//, '').split('?')[0] || null;

        // Case 2: hub with ?g=<slug>
        const searchParams = new URLSearchParams(search);
        const querySlug = searchParams.get('g');

        // If the current URL slug differs from the record slug, jump straight to the record slug.
        if (currentSlug && resourceSlug && currentSlug === resourceSlug && currentSlug !== recordSlug) {
          window.location.replace(`/graves/${recordSlug}${search}`);
          return;
        }

        // If the hub ?g= slug differs, update it.
        if (querySlug && resourceSlug && querySlug === resourceSlug && querySlug !== recordSlug) {
          const params = new URLSearchParams(searchParams);
          params.set('g', recordSlug);
          window.location.replace(`/graves?${params.toString()}`);
          return;
        }
      }
    }
  } catch {}
};

export default handleCacheInvalidationMessage;
